import type { BlockStyle, PriceBookContent } from '@/blocks/domain/types';
import { TEXT_COLORS } from '@/common/presentation/constants/colors';
import { buildPriceBookSectionsView } from '@/document-render/domain/priceBook/buildPriceBookSectionsView';
import { measuredFontSizeCss } from '@/document-render/application/blockFontSize';
import {
  RATE_TABLE_STYLE,
  SUBSECTION_LABEL_STYLE,
  TITLE_STYLE,
  priceBookSectionStyle,
  rateBodyCellStyle,
  rateHeadCellStyle
} from './priceBookSheetStyles';

/**
 * Unbound authoring shape - the HTML twin of `PriceBookSectionsPdf`: three tables, each with its
 * own first column and one never-split row of locked tokens (meta tokens one per line).
 */
export function PriceBookSectionsShape({ content, style }: { content: PriceBookContent; style: BlockStyle }) {
  const sections = buildPriceBookSectionsView(content);
  return (
    <section aria-label="block-price-book" style={priceBookSectionStyle(style)}>
      {content.title ? <div style={TITLE_STYLE}>{content.title}</div> : null}
      {sections.map((section) => (
        <div key={section.key} data-price-book-section={section.key} style={{ breakInside: 'avoid', marginBottom: 6 }}>
          <div style={SUBSECTION_LABEL_STYLE}>{section.label}</div>
          <table style={RATE_TABLE_STYLE}>
            <thead>
              <tr>
                <th scope="col" style={rateHeadCellStyle('left', measuredFontSizeCss(style))}>
                  {section.rowHeader}
                </th>
                {section.columns.map((column) => (
                  <th key={column.key} scope="col" style={rateHeadCellStyle('right', measuredFontSizeCss(style))}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={rateBodyCellStyle('left', true)}>
                  <div data-locked-token={section.nameToken}>{section.nameToken}</div>
                  {section.meta.map((meta) => (
                    <div key={meta.key} data-locked-token={meta.token} style={{ fontSize: 11, fontWeight: 400, color: TEXT_COLORS.MUTED }}>
                      {meta.token}
                    </div>
                  ))}
                </td>
                {section.columns.map((column) => (
                  <td key={column.key} data-locked-token={column.token} style={rateBodyCellStyle('right', false)}>
                    {column.token}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}
